import { api } from './client'
import type { NeedCategory } from '../types/api'

/**
 * The shared taxonomy, as served by GET /api/taxonomy.
 *
 * This is a CONTRACT, not a convenience: a need and a service only match when their option
 * strings are byte-identical, and the backend rejects anything outside these lists rather than
 * coercing it. So the UI must render its pickers from this payload and never from a local copy -
 * a hard-coded list that drifts produces options the user can pick and the server will refuse.
 */
export interface TaxonomyCategory {
  /** The value to send as `category` on /api/needs and /api/services (e.g. "SPORTS_FITNESS"). */
  name: NeedCategory
  /** Display slug used by the portal (e.g. "sports"). Not accepted by the write endpoints. */
  key: string
  /** What this category calls its items ("Subjects", "Sport", ...). */
  itemsLabel: string
  /** The matchable list: a learner wants some of these, a provider teaches some of these. */
  items: string[]
  /** Per-category question keys mapped to their allowed answers, e.g. { class: [...] }. */
  detail: Record<string, string[]>
  /** Lists that only apply once a parent answer is chosen (dance forms under Arts). */
  dependent: Record<string, string[]>
  /** Allowed levels, or null where the category is graded by class instead. */
  levels: string[] | null
}

export interface TaxonomyPayload {
  taxonomy_version: string
  /** Keyed by display label, e.g. "Academics (School Subjects)". */
  categories: Record<string, TaxonomyCategory>
  common: {
    mode: string[]
    timing: string[]
    budget_band: string[]
    preferred_tutor_gender: string[]
    levels: string[]
    /** Weekdays a provider is available. Provider-side only - no need states a weekday. */
    day: string[]
    /** Medium of instruction, which is not the Languages category. */
    teaching_language: string[]
  }
  registration_pills: string[]
  band_to_classes: Record<string, string[]>
}

export function fetchTaxonomy(): Promise<TaxonomyPayload> {
  return api.get<TaxonomyPayload>('/taxonomy')
}
